import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import toast from "react-hot-toast";
import {
  AddWarehousePayload,
  AddWarehouseResponse,
  CreateStatePayload,
  CreateStateResponse,
  State,
  UpdateStorePayload,
  UpdateStoreResponse,
} from "../types/types";
import {
  GetRidersPendingResponse,
  GetRiderStatsResponse,
  GetRiderByIdResponse,
  ApproveRiderResponse,
  RejectRiderResponse,
  SuspendRiderRequest,
  UpdateRiderRequest,
  GetRidersOverviewResponse,
  GetAllRidersStatsResponse,
  GetRecentActivitiesResponse,
  GetAvailableRidersResponse,
  GetRiderStatsOverviewResponse,
  GetRealTimeStatsResponse,
} from "../types/rider";
import {
  IssuesReportsResponse,
  GetIssuesReportsParams,
  UpdateIssueStatusRequest,
  UpdateIssueStatusResponse,
} from "../types/issues";
import {
  LogisticsOrdersResponse,
  NewOrdersByCustomerResponse,
} from "../types/orders";
import {
  BroadcastMessagesResponse,
  CreateBroadcastMessageRequest,
  CreateBroadcastMessageResponse,
  UpdateBroadcastMessageRequest,
  IndividualMessagesResponse,
  CreateIndividualMessageRequest,
  CreateIndividualMessageResponse,
  UpdateIndividualMessageRequest,
  TargetAudienceMessagesResponse,
  CreateTargetAudienceMessageRequest,
  CreateTargetAudienceMessageResponse,
  UpdateTargetAudienceMessageRequest,
} from "../types/communication";
import {
  GetPendingWithdrawalsResponse,
  GetWithdrawalDetailsResponse,
  GetEarningPayoutStatsResponse,
  UpdatePayoutStatusRequest,
  UpdatePayoutStatusResponse,
} from "../types/payout";
import {
  GetRidersPerformanceResponse,
  GetRidersStatsResponse,
} from "../types/performance";
import {
  CreateCampaignPayload,
  CreateDiscountPayload,
  CreateFreeDeliveryPayload,
  CreatePromoCodePayload,
  CreatePromoCodeFunctionPayload,
  UpdatePromoCodeFunctionPayload,
  CreateDiscountFunctionPayload,
  UpdateDiscountFunctionPayload,
  CreateFreeDeliveryFunctionPayload,
  UpdateFreeDeliveryFunctionPayload,
  UpdateGamificationConfigPayload,
  GamificationConfigResponse,
  CreateCheckoutCategoryPayload,
  CheckoutCategoriesResponse,
  SingleCheckoutCategoryResponse,
} from "../types/rewards";
import {
  GetAllContactMessagesResponse,
  GetSingleContactMessageResponse,
  ReplyContactMessageResponse,
} from "../types/contactMessages";

const auth: any = sessionStorage.getItem("authState");
const jsonData = JSON.parse(auth);
const token = jsonData?.token;

export const login = createAsyncThunk(
  "login",
  async (credentials: { email: string; password: string }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/admins/login`,
        credentials,
      );

      if (response.data.admin) {
        sessionStorage.setItem("adminLevel", response.data.admin.adminLevel);
        // Remove passwordHash before returning to store
        delete response.data.admin.passwordHash;
      }

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Login failed",
      );
    }
  },
);

export const getAllProducts = createAsyncThunk(
  "getAllProducts",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-products`,
      );
      return response.data.reverse();
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

export const getPendingProducts = createAsyncThunk(
  "products/getPendingProducts",
  async (
    { page = 1, limit = 10 }: { page?: number; limit?: number } = {},
    thunkAPI,
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/pending-products?page=${page}&limit=${limit}`,
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || "Failed to fetch pending products",
      );
    }
  },
);

export const getPendingProductById = createAsyncThunk(
  "products/getPendingProductById",
  async (id: string, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-change-request/${id}`,
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || "Failed to fetch pending product",
      );
    }
  },
);

export const approveProduct = createAsyncThunk(
  "products/approveProduct",
  async (productId: string, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/approve-product/${productId}`,
        undefined,
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || "Failed to approve product",
      );
    }
  },
);

export const rejectProduct = createAsyncThunk(
  "products/rejectProduct",
  async (
    { productId, rejectionReason }: { productId: string; rejectionReason: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/reject-product/${productId}`,
        { rejectionReason },
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.error || error.response?.data?.message || "Failed to reject product",
      );
    }
  },
);

export const approveAllProducts = createAsyncThunk(
  "products/approveAllProducts",
  async ({ productIds }: { productIds: string[] }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/approve-product/all`,
        // Backend validation is inconsistent (some envs expect productIds, others productIDs).
        // Send both to be safe.
        { productIds, productIDs: productIds },
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to approve all products",
      );
    }
  },
);

export const createDiscountProduct = createAsyncThunk(
  "products/createDiscountProduct",
  async (
    {
      name,
      measurement,
      type,
      productTypeId,
      price,
      location,
      quantity,
      image,
      images,
      description,
      discountPrice,
    }: {
      name: string;
      measurement: string;
      type: string;
      productTypeId: string;
      price: number;
      location: string;
      quantity: number;
      image: string;
      images: string[];
      description: string;
      discountPrice: number;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/create-discount-product`,
        {
          name,
          measurement,
          type,
          productTypeId,
          price,
          location,
          quantity,
          image,
          images,
          description,
          discount: {
            isDiscount: true,
            price: discountPrice,
          },
        },
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to create discount product",
      );
    }
  },
);

export const createDiscountProductFromExisting = createAsyncThunk(
  "products/createDiscountProductFromExisting",
  async (
    {
      existingProductId,
      productTypeId,
      discountProductPrice,
      quantity,
      measurement,
      description,
    }: {
      existingProductId: string;
      productTypeId: string;
      discountProductPrice: number;
      quantity: number;
      measurement: string;
      description: string;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/products/create-from-existing`,
        {
          existingProductId,
          productTypeId,
          discountedPrice: discountProductPrice,
          quantity,
          measurement,
          description,
        },
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to create discount product from existing",
      );
    }
  },
);

export const getDiscountProducts = createAsyncThunk(
  "products/getDiscountProducts",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discount-products?limit=1000`,
        {
          headers: token
            ? {
              Authorization: `Bearer ${token}`,
            }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch discount products",
      );
    }
  },
);

export const getProduct = createAsyncThunk(
  "getProduct",
  async (productId: any, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-product/${productId}`,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

export const getAllProductType = createAsyncThunk(
  "getAllProductType",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllProductType?limit=50`,
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);

export const updateSingleProduct = createAsyncThunk(
  "updateProduct",
  async ({ data, id }: { data: any; id: string }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-product/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const updateProductType = createAsyncThunk(
  "updateProductType",
  async ({ image, id }: { image: string; id: string }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/updateProductTypeImage/${id}`,
        { image },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteProductType = createAsyncThunk(
  "deleteProductType",
  async (categoryId: string, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteProductType/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteProduct = createAsyncThunk(
  "deleteProduct",
  async (productId: string, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/delete-product/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const createProductItem = createAsyncThunk(
  "createProductItem",
  async (data: any, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/create-product`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.error);
    }
  },
);
export const createProductType = createAsyncThunk(
  "createProductType",
  async (data: any, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/createProductType`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

// ─── Sub-Category CRUD ───────────────────────────────────────────────

export const getSubCategories = createAsyncThunk(
  "subCategories/getAll",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories`,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategories",
      );
    }
  },
);

export const getSubCategory = createAsyncThunk(
  "subCategories/getOne",
  async (id: string, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/${id}`,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategory",
      );
    }
  },
);

export const createSubCategory = createAsyncThunk(
  "subCategories/create",
  async (
    data: { productTypeId: string; name: string; isActive?: boolean },
    thunkAPI,
  ) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/sub-categories`,
        data,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to create subcategory",
      );
    }
  },
);

export const updateSubCategory = createAsyncThunk(
  "subCategories/update",
  async ({ id, name }: { id: string; name: string }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/sub-categories/${id}`,
        { name },
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to update subcategory",
      );
    }
  },
);

export const deleteSubCategory = createAsyncThunk(
  "subCategories/delete",
  async (id: string, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/sub-categories/${id}`,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to delete subcategory",
      );
    }
  },
);

export const assignProductToSubCategory = createAsyncThunk(
  "subCategories/assignProduct",
  async (
    {
      productId,
      productTypeId,
      quantity,
      subProductTypeId,
    }: {
      productId: string;
      productTypeId: string;
      quantity: number;
      subProductTypeId: string;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/sub-categories/groceries/${productId}`,
        { productTypeId, quantity, subProductTypeId },
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to assign product to subcategory",
      );
    }
  },
);

export const getSubCategoryProducts = createAsyncThunk(
  "subCategories/getProducts",
  async (
    {
      subCategoryId,
      page = 1,
      limit = 20,
    }: { subCategoryId: string; page?: number; limit?: number },
    thunkAPI,
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/sub-categories/${subCategoryId}/pagination?page=${page}&limit=${limit}`,
        {
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch subcategory products",
      );
    }
  },
);

// ─── End Sub-Category ────────────────────────────────────────────────

export const verificationStageOne = createAsyncThunk(
  "verificationStageOne",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/verification-stage-one`,
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getOrders = createAsyncThunk(
  "getOrders",
  async ({ page, limit, categoryType, orderType }: any, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllOrdersByCustomer?page=${page}&limit=${limit}&categoryType=${categoryType}&orderType=${orderType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

// Get orders filtered by status from backend (for performance optimization)
export const getOrdersByStatus = createAsyncThunk(
  "getOrdersByStatus",
  async (
    {
      page,
      limit,
      categoryType,
      orderType,
      status,
    }: {
      page: number;
      limit: number;
      categoryType: string;
      orderType: string;
      status: string;
    },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllOrdersByCustomerStatus?page=${page}&limit=${limit}&categoryType=${categoryType}&orderType=${orderType}&status=${status}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getNewOrdersByCustomer = createAsyncThunk<
  NewOrdersByCustomerResponse,
  { month: number; year: number },
  { rejectValue: string }
>("orders/getNewOrdersByCustomer", async ({ month, year }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/getAllNewOrdersByCustomer?month=${month}&year=${year}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as NewOrdersByCustomerResponse;
  } catch (error: any) {
    const errMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch new orders";
    return thunkAPI.rejectWithValue(errMsg);
  }
});

export const getSingleCustomer = createAsyncThunk(
  "getSingleCustomer",
  async (id: string, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getSingleCustomer/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllRepaymentHistory = createAsyncThunk(
  "getAllRepaymentHistory",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllRepaymentHistory`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const allCustomerNextPay = createAsyncThunk(
  "allCustomerNextPay",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/allCustomerNextPay`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getDefaulters = createAsyncThunk(
  "getDefaulters",
  async ({ page, limit, categoryType, search }: any, thunkAPI) => {
    try {
      const query = new URLSearchParams();
      query.set("categoryType", String(categoryType));

      if (search && String(search).trim().length > 0) {
        query.set("email", String(search).trim());
      } else {
        query.set("page", String(page));
        query.set("limit", String(limit));
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/defaulters?${query.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllDefaulters = createAsyncThunk(
  "getAllDefaulters",
  async (categoryType: any, thunkAPI) => {
    try {
      let allUsers: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages
      do {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/defaulters?page=${currentPage}&limit=100&categoryType=${categoryType}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (response.data && response.data.data) {
          allUsers = [...allUsers, ...response.data.data];
          totalPages = response.data?.totalPages || 1;
        }

        currentPage++;
      } while (currentPage <= totalPages);

      return {
        data: allUsers,
        pagination: {
          total: allUsers.length,
          totalPages: Math.ceil(allUsers.length / 50),
        },
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const approveOrder = createAsyncThunk(
  "approveOrder",
  async (data: any, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/approve-order`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to approve order";
      return thunkAPI.rejectWithValue({
        status,
        message,
        data: error?.response?.data,
      });
    }
  },
);

export const getAllBanner = createAsyncThunk(
  "getAllBanner",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllBanner`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const deleteBanner = createAsyncThunk(
  "deleteBanner",
  async (id: string, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/deleteBanner/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllDispatch = createAsyncThunk(
  "getAllDispatch",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllDispatcherDetails`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const logisticDashboard = createAsyncThunk(
  "logisticDashboard",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/logisticDashboardStatistic`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const logisticAssignDetails = createAsyncThunk(
  "logisticAssignDetails",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getDetailsForLogisticAssignDelivery`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const createDispatcher = createAsyncThunk(
  "createDispatcher",
  async (
    dispatcherData: {
      firstName: string;
      lastName: string;
      email: string;
      password: string;
      phoneNumber: string;
      image: string;
    },
    thunkAPI,
  ) => {
    try {
      // Ensure the image field is included
      const payload = {
        ...dispatcherData,
        image:
          dispatcherData.image ||
          "https://jy0s2swu0k.ufs.sh/f/LKSiq2B5GRS8nqXTASCYrqb8EcQRiuAhmfJoHsDWN5U347eg",
      };

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/createDispatcher`,
        payload,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error creating dispatcher:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to create dispatcher",
      );
    }
  },
);

export const getAllVendors = createAsyncThunk(
  "getAllVendors",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/vendors`, {
        headers: {
          Authorization: token,
        },
      });
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllStates = createAsyncThunk<
  State[],
  void,
  { rejectValue: string }
>("warehouse/getAllStates", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/getAllStates`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data as State[];
  } catch (error: any) {
    console.error("Error fetching states:", error);
    const errorMsg =
      error.response?.data?.error || error.message || "Failed to fetch states";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= COMMUNICATION CENTER THUNKS =============

// ===== BROADCAST MESSAGES =====

// Get broadcast messages
export const getBroadcastMessages = createAsyncThunk<
  BroadcastMessagesResponse,
  void,
  { rejectValue: string }
>("communication/getBroadcastMessages", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/broadcast`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as BroadcastMessagesResponse;
  } catch (error: any) {
    console.error("Error fetching broadcast messages:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch broadcast messages";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// Create broadcast message
export const createBroadcastMessage = createAsyncThunk<
  CreateBroadcastMessageResponse,
  CreateBroadcastMessageRequest,
  { rejectValue: string }
>("communication/createBroadcastMessage", async (messageData, thunkAPI) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/broadcast`,
      messageData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    toast.success("Broadcast message sent successfully!");
    return response.data as CreateBroadcastMessageResponse;
  } catch (error: any) {
    console.error("Error creating broadcast message:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to create broadcast message";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// Update broadcast message
export const updateBroadcastMessage = createAsyncThunk<
  { message: string },
  { messageId: string; data: UpdateBroadcastMessageRequest },
  { rejectValue: string }
>(
  "communication/updateBroadcastMessage",
  async ({ messageId, data }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_LOGISTIC_URL}/message/broadcast/${messageId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Broadcast message updated successfully!");
      return response.data;
    } catch (error: any) {
      console.error(`Error updating broadcast message ${messageId}:`, error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update broadcast message";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// Delete broadcast message
export const deleteBroadcastMessage = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("communication/deleteBroadcastMessage", async (messageId, thunkAPI) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/broadcast/${messageId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    toast.success("Broadcast message deleted successfully!");
    return response.data;
  } catch (error: any) {
    console.error(`Error deleting broadcast message ${messageId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete broadcast message";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ===== INDIVIDUAL MESSAGES =====

// Get individual messages
export const getIndividualMessages = createAsyncThunk<
  IndividualMessagesResponse,
  void,
  { rejectValue: string }
>("communication/getIndividualMessages", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/individual`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as IndividualMessagesResponse;
  } catch (error: any) {
    console.error("Error fetching individual messages:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch individual messages";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// Create individual message
export const createIndividualMessage = createAsyncThunk<
  CreateIndividualMessageResponse,
  { riderId: string; data: CreateIndividualMessageRequest },
  { rejectValue: string }
>(
  "communication/createIndividualMessage",
  async ({ riderId, data }, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LOGISTIC_URL}/message/individual/${riderId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Individual message sent successfully!");
      return response.data as CreateIndividualMessageResponse;
    } catch (error: any) {
      console.error(
        `Error creating individual message for rider ${riderId}:`,
        error,
      );
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to create individual message";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// Update individual message
export const updateIndividualMessage = createAsyncThunk<
  { message: string },
  { messageId: string; data: UpdateIndividualMessageRequest },
  { rejectValue: string }
>(
  "communication/updateIndividualMessage",
  async ({ messageId, data }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_LOGISTIC_URL}/message/individual/${messageId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Individual message updated successfully!");
      return response.data;
    } catch (error: any) {
      console.error(`Error updating individual message ${messageId}:`, error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update individual message";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// Delete individual message
export const deleteIndividualMessage = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("communication/deleteIndividualMessage", async (messageId, thunkAPI) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/individual/${messageId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    toast.success("Individual message deleted successfully!");
    return response.data;
  } catch (error: any) {
    console.error(`Error deleting individual message ${messageId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete individual message";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ===== TARGET AUDIENCE MESSAGES =====

// Get target audience messages
export const getTargetAudienceMessages = createAsyncThunk<
  TargetAudienceMessagesResponse,
  void,
  { rejectValue: string }
>("communication/getTargetAudienceMessages", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/top-performers`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as TargetAudienceMessagesResponse;
  } catch (error: any) {
    console.error("Error fetching target audience messages:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch target audience messages";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// Create target audience message
export const createTargetAudienceMessage = createAsyncThunk<
  CreateTargetAudienceMessageResponse,
  CreateTargetAudienceMessageRequest,
  { rejectValue: string }
>(
  "communication/createTargetAudienceMessage",
  async (messageData, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_LOGISTIC_URL}/message/top-performers`,
        messageData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Target audience message sent successfully!");
      return response.data as CreateTargetAudienceMessageResponse;
    } catch (error: any) {
      console.error("Error creating target audience message:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to create target audience message";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// Update target audience message
export const updateTargetAudienceMessage = createAsyncThunk<
  { message: string },
  { messageId: string; data: UpdateTargetAudienceMessageRequest },
  { rejectValue: string }
>(
  "communication/updateTargetAudienceMessage",
  async ({ messageId, data }, thunkAPI) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_LOGISTIC_URL}/message/top-performer/${messageId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      toast.success("Target audience message updated successfully!");
      return response.data;
    } catch (error: any) {
      console.error(
        `Error updating target audience message ${messageId}:`,
        error,
      );
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update target audience message";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// Delete target audience message
export const deleteTargetAudienceMessage = createAsyncThunk<
  { message: string },
  string,
  { rejectValue: string }
>("communication/deleteTargetAudienceMessage", async (messageId, thunkAPI) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_LOGISTIC_URL}/message/top-performer/${messageId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    toast.success("Target audience message deleted successfully!");
    return response.data;
  } catch (error: any) {
    console.error(
      `Error deleting target audience message ${messageId}:`,
      error,
    );
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to delete target audience message";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const getStateById = createAsyncThunk<
  State,
  string,
  { rejectValue: string }
>("warehouse/getStateById", async (stateId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }

  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/getStateById/${stateId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return res.data as State;
  } catch (error: any) {
    console.error(`Error fetching state ${stateId}:`, error);
    const errorMsg =
      error.response?.data?.error ||
      error.message ||
      "Failed to fetch state details";

    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const updateWarehouseStore = createAsyncThunk<
  UpdateStoreResponse,
  UpdateStorePayload,
  { rejectValue: string }
>("warehouse/updateStore", async ({ stateId, storeId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!stateId) {
    return thunkAPI.rejectWithValue("State ID is missing for update.");
  }
  if (!storeId) {
    return thunkAPI.rejectWithValue("Store ID is missing for update.");
  }

  try {
    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/stateManagement/${stateId}/warehouse/${storeId}`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    if (response.data && response.data.updatedState) {
      console.log(
        "Thunk: Update successful, received updatedState:",
        response.data.updatedState,
      );
      return response.data.updatedState as UpdateStoreResponse;
    } else if (response.data && response.data._id && response.data.wareHouse) {
      console.log(
        "Thunk: Update successful, received State directly:",
        response.data,
      );
      return response.data as UpdateStoreResponse;
    } else {
      console.warn(
        "Thunk: Update response structure unexpected:",
        response.data,
      );
      return thunkAPI.rejectWithValue(
        "Invalid response structure from update API.",
      );
    }
  } catch (error: any) {
    console.error(
      `Error updating store (StateID: ${stateId}, StoreID: ${storeId}):`,
      error,
    );
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to update store";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const createStateAndWarehouse = createAsyncThunk<
  CreateStateResponse,
  CreateStatePayload,
  { rejectValue: string }
>("warehouse/createState", async (payload, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!payload) {
    return thunkAPI.rejectWithValue("Data Cannot be empty.");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/createStateManagement`,
      payload,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return response.data as CreateStateResponse;
  } catch (error: any) {
    console.error("Error creating state:", error);
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to create state";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const addWarehouseToState = createAsyncThunk<
  AddWarehouseResponse,
  AddWarehousePayload,
  { rejectValue: string }
>("warehouse/addStoreToState", async ({ stateId, data }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  if (!token) {
    return thunkAPI.rejectWithValue("Authentication token not found.");
  }
  if (!stateId) {
    return thunkAPI.rejectWithValue("State ID is missing.");
  }

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/stateManagement/${stateId}/warehouse`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (response.data && response.data.updatedState) {
      return response.data.updatedState as AddWarehouseResponse;
    } else {
      return thunkAPI.rejectWithValue(
        "Invalid response structure from add warehouse API.",
      );
    }
  } catch (error: any) {
    console.error("Error adding warehouse to state:", error);
    const errorMsg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Failed to add warehouse";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const getFees = createAsyncThunk("getFees", async (_, thunkAPI) => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/get-allFees`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return res.data;
  } catch (error: any) {
    console.error(error);
    return thunkAPI.rejectWithValue(error.response?.data || error.message);
  }
});

// Toggle free delivery on first order for a specific fee record
export const toggleFreeDelivery = createAsyncThunk(
  "toggleFreeDelivery",
  async (feeId: string, thunkAPI) => {
    try {
      const res = await axios.patch(
        `${process.env.REACT_APP_API_URL}/toggleFreeDelivery/${feeId}`,
        {},
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ============= RESTAURANT FEE MANAGEMENT THUNKS =============

// Fetch global restaurant fees
export const fetchRestaurantGlobalFees = createAsyncThunk(
  "restaurantFees/fetchGlobalFees",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/global-fees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error fetching restaurant global fees:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch global fees",
      );
    }
  },
);

// Update global restaurant fees
export const updateRestaurantGlobalFees = createAsyncThunk(
  "restaurantFees/updateGlobalFees",
  async (
    payload: {
      commissionRate: number;
      deliveryRate: number;
      serviceCharge: number;
    },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_RESTAURANT_URL}/global-fees`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error updating restaurant global fees:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to update global fees",
      );
    }
  },
);

// Fetch specific restaurant fee
export const fetchRestaurantSpecificFee = createAsyncThunk(
  "restaurantFees/fetchRestaurantFee",
  async (restaurantId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurant-fee/${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return { restaurantId, ...res.data };
    } catch (error: any) {
      console.error("Error fetching restaurant fee:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch restaurant fee",
      );
    }
  },
);

// Update specific restaurant fee
export const updateRestaurantSpecificFee = createAsyncThunk(
  "restaurantFees/updateRestaurantFee",
  async (
    {
      restaurantId,
      payload,
    }: {
      restaurantId: string;
      payload: {
        commissionRate: number;
        deliveryRate: number;
        serviceCharge: number;
      };
    },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_RESTAURANT_URL}/restaurant-fee/${restaurantId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error updating restaurant fee:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to update restaurant fee",
      );
    }
  },
);

// Fetch restaurant fee history
export const fetchRestaurantFeeHistory = createAsyncThunk(
  "restaurantFees/fetchFeeHistory",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/fee-history`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error fetching restaurant fee history:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch fee history",
      );
    }
  },
);

// ============= RESTAURANT ISSUES MANAGEMENT THUNKS =============

// Fetch all restaurant issues
export const fetchAllRestaurantIssues = createAsyncThunk(
  "restaurantIssues/fetchAllIssues",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/getAllIssues`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error fetching all restaurant issues:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch issues",
      );
    }
  },
);

// Fetch issues for a specific restaurant
export const fetchRestaurantIssues = createAsyncThunk(
  "restaurantIssues/fetchRestaurantIssues",
  async (restaurantId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_RESTAURANT_URL}/getMyIssues/${restaurantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error fetching restaurant issues:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch restaurant issues",
      );
    }
  },
);

// Update restaurant issue (respond and change status)
export const updateRestaurantIssue = createAsyncThunk(
  "restaurantIssues/updateIssue",
  async (
    {
      issueId,
      responseMessage,
      status,
    }: {
      issueId: string;
      responseMessage: string;
      status: string;
    },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_RESTAURANT_URL}/issues/${issueId}/respond`,
        {
          responseMessage,
          status,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error updating restaurant issue:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to update issue",
      );
    }
  },
);

// ============= LOGISTICS RIDER MANAGEMENT THUNKS =============

// Create a new rider
export const createRider = createAsyncThunk(
  "logistics/createRider",
  async (
    payload: {
      fullName: string;
      email: string;
      password: string;
      confirmPassword: string;
      phoneNumber: string;
      riderInfo: {
        idUpload: string;
        lisenceNumber: string;
        plateNumber: string;
        model: string;
        homeAddress: string;
      };
      bankDetails: Array<{
        bankName: string;
        accountName: string;
        accountNumber: number;
        bankCode: number;
      }>;
    },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_LOGISTIC_URL}/create-rider`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error("Error creating rider:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to create rider",
      );
    }
  },
);

export const getFlexibleUsersPaginated = createAsyncThunk(
  "getFlexibleUsersPaginated",
  async ({ page, limit, search }: any, thunkAPI) => {
    try {
      const query = new URLSearchParams();
      if (search && String(search).trim().length > 0) {
        query.set("email", String(search).trim());
      } else {
        query.set("page", String(page));
        query.set("limit", String(limit));
      }
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getapprovedCustomers?${query.toString()}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getFlexibleUsers = createAsyncThunk(
  "getAllFlexibleUsers",
  async (_, thunkAPI) => {
    try {
      let allUsers: any[] = [];
      let currentPage = 1;
      let totalPages = 1;

      // Fetch all pages
      do {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getapprovedCustomers?page=${currentPage}&limit=100`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        if (res.data && res.data.data) {
          allUsers = [...allUsers, ...res.data.data];
          totalPages = res.data.pagination?.totalPages || 1;
        }

        currentPage++;
      } while (currentPage <= totalPages);

      return {
        data: allUsers,
        pagination: {
          total: allUsers.length,
          totalPages: Math.ceil(allUsers.length / 50),
        },
      };
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getEventsGroupedByUser = createAsyncThunk(
  "getEventsGroupedByUser",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getEventsGroupedByUser`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch transactions",
      );
    }
  },
);

export const getAllPaystackEvents = createAsyncThunk(
  "getAllPaystackEvents",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllPaystackEvents`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch paystack events",
      );
    }
  },
);

export const getOutrightUsers = createAsyncThunk(
  "getOutrightUsers",
  async ({ page, limit, search }: any, thunkAPI) => {
    try {
      const query = new URLSearchParams();
      if (search && String(search).trim().length > 0) {
        query.set("email", String(search).trim());
      } else {
        query.set("page", String(page));
        query.set("limit", String(limit));
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getOutrightCustomers?${query.toString()}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllOutrightUsers = createAsyncThunk(
  "getAllOutrightUsers",
  async (_, thunkAPI) => {
    let allUsers: any[] = [];
    let currentPage = 1;
    let totalPages = 1;

    // Fetch all pages
    do {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getOutrightCustomers?page=${currentPage}&limit=50`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        const resData = res.data;

        // Handle multiple possible response formats
        const rows =
          resData?.results ||
          resData?.data ||
          resData?.customers ||
          resData?.users ||
          (Array.isArray(resData) ? resData : []);

        if (Array.isArray(rows) && rows.length > 0) {
          allUsers = [...allUsers, ...rows];
        }

        // Extract totalPages from various possible locations
        totalPages =
          resData?.pagination?.totalPages ||
          resData?.totalPages ||
          resData?.meta?.totalPages ||
          1;




        if (currentPage >= 100 || (Array.isArray(rows) && rows.length === 0)) {
          break;
        }

        currentPage++;
      } catch (error: any) {
        console.error(`[getAllOutrightUsers] Error on page ${currentPage}:`, error);
        break;
      }
    } while (currentPage <= totalPages);

    return {
      data: allUsers,
      pagination: {
        total: allUsers.length,
        totalPages: Math.ceil(allUsers.length / 50),
      },
    };
  },
);

export const getAllIncompleteUsers = createAsyncThunk(
  "getAllIncompleteUsers",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/incompleteRegisteration`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getStateGovernmentUsers = createAsyncThunk(
  "getStateGovernmentUsers",
  async ({ page, limit, search }: any, thunkAPI) => {
    try {
      const query = new URLSearchParams();
      if (search && String(search).trim().length > 0) {
        query.set("email", String(search).trim());
      } else {
        query.set("page", String(page));
        query.set("limit", String(limit));
      }

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getStateGovernmentUsers?${query.toString()}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );

      return res.data;
    } catch (error: any) {
      console.error(error);
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const getAllStateUsers = createAsyncThunk(
  "getAllStateUsers",
  async (_, thunkAPI) => {
    let allUsers: any[] = [];
    let currentPage = 1;
    let totalPages = 1;

    // Fetch all pages
    do {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/getStateGovernmentUsers?page=${currentPage}&limit=50`,
          {
            headers: {
              Authorization: token,
            },
          },
        );

        const resData = res.data;

        // Handle multiple possible response formats
        const rows =
          resData?.users ||
          resData?.data ||
          resData?.results ||
          resData?.customers ||
          (Array.isArray(resData) ? resData : []);

        if (Array.isArray(rows) && rows.length > 0) {
          allUsers = [...allUsers, ...rows];
        }

        // Extract totalPages
        totalPages =
          resData?.pagination?.totalPages ||
          resData?.totalPages ||
          resData?.meta?.totalPages ||
          1;

        // Failsafe
        if (currentPage >= 100 || (Array.isArray(rows) && rows.length === 0)) {
          break;
        }

        currentPage++;
      } catch (error: any) {
        console.error(`[getAllStateUsers] Error on page ${currentPage}:`, error);
        break; // Stop fetching, but return what we have so far
      }
    } while (currentPage <= totalPages);

    return {
      data: allUsers,
      pagination: {
        total: allUsers.length,
        totalPages: Math.ceil(allUsers.length / 50),
      },
    };
  },
);

// Blacklist thunks
export const getAllBlacklisted = createAsyncThunk(
  "getAllBlacklisted",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getAllBlacklisted`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch blacklist",
      );
    }
  },
);

export const addToBlacklist = createAsyncThunk(
  "addToBlacklist",
  async ({ email }: { email: string }, thunkAPI) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/addToBlacklist`,
        { email },
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to add user to blacklist",
      );
    }
  },
);

export const removeFromBlacklist = createAsyncThunk(
  "removeFromBlacklist",
  async ({ email }: { email: string }, thunkAPI) => {
    try {
      const res = await axios.delete(
        `${process.env.REACT_APP_API_URL}/removeFromBlacklist`,
        {
          headers: {
            Authorization: token,
          },
          data: { email },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to remove user from blacklist",
      );
    }
  },
);

export const getBlacklistHistoryByEmail = createAsyncThunk(
  "getBlacklistHistoryByEmail",
  async ({ email }: { email: string }, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getBlacklistHistoryByEmail?email=${email}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch blacklist history",
      );
    }
  },
);

// Agent Activity Logs thunk
export const getAgentActivityLogs = createAsyncThunk(
  "getAgentActivityLogs",
  async (
    params: { agentId?: string; startDate?: string; endDate?: string } = {},
    thunkAPI,
  ) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();
      if (params.agentId) queryParams.append("agentId", params.agentId);
      if (params.startDate) queryParams.append("startDate", params.startDate);
      if (params.endDate) queryParams.append("endDate", params.endDate);

      const queryString = queryParams.toString();
      const url = `${process.env.REACT_APP_API_URL}/agents/activity-logs${queryString ? `?${queryString}` : ""
        }`;

      const res = await axios.get(url, {
        headers: {
          Authorization: token,
        },
      });
      // Ensure we return an array, even if the API returns a different structure
      return Array.isArray(res.data) ? res.data : [];
    } catch (error: any) {
      console.error("Error fetching agent activity logs:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch agent activity logs",
      );
    }
  },
);

export const getRepaymentHistoryByCustomerId = createAsyncThunk(
  "getRepaymentHistoryByCustomerId",
  async (customerId: string, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/getRepaymentHistoryByCustomerId/${customerId}`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      return res.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch repayment history",
      );
    }
  },
);

export const checkTokenExpiration = () => {
  const tokenTimestamp = sessionStorage.getItem("tokenTimestamp");
  if (auth && token && tokenTimestamp) {
    const currentTime = new Date().getTime();
    const expirationTime =
      new Date(parseInt(tokenTimestamp, 10)).getTime() + 24 * 60 * 60 * 1000;

    if (currentTime >= expirationTime) {
      sessionStorage.clear();
      sessionStorage.removeItem("tokenTimestamp");
      return false;
    }
    return true;
  }
  return false;
};

export const formatNumber = (number: number) => {
  return new Intl.NumberFormat().format(number);
};

export const getDashboardStats = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/getDashboardStatistic`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch:", error);
  }
};

export const verificationStageTwo = createAsyncThunk(
  "verificationStageTwo",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/verification-stage-two`,
      );

      return response.data.customers.filter(
        (user: any) => user.accountType === "flexible",
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchCategories = async () => {
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/onboardingCategories`,
      {
        headers: {
          Authorization: token,
        },
      },
    );
    const data = res.data.categories.filter(
      (item: any) => item.category.toLowerCase() === "oyo state government",
    );
    return data[0]?.categoryType;
  } catch (error) {
    console.error("An error occurred: ", error);
  }
};

export const fetchStateGovernmentDetails = createAsyncThunk(
  "fetchStateGovernmentDetails",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/onboardingCategories`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const data = res.data.categories.filter(
        (item: any) => item.category.toLowerCase() === "oyo state government",
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchAllCategories = createAsyncThunk(
  "fetchAllCategories",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/onboardingCategories`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const data = res.data.categories;
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchChurchDetails = createAsyncThunk(
  "fetchChurchDetails",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/onboardingCategories`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const data = res.data.categories.filter(
        (item: any) => item.category.toLowerCase() === "church",
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchPrivateOrgDetails = createAsyncThunk(
  "fetchPrivateOrgDetails",
  async (_, thunkAPI) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/onboardingCategories`,
        {
          headers: {
            Authorization: token,
          },
        },
      );
      const data = res.data.categories.filter(
        (item: any) => item.category.toLowerCase() === "private organization",
      );
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data);
    }
  },
);

export const fetchStateUsersWithOrders = async (
  currentPage: number,
  itemsPerPage: number,
  pageNo: number | null = null,
) => {
  const pageNumber = pageNo ? pageNo : currentPage;
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/getStateGovernmentCustomersWithOrders?page=${pageNumber}&limit=${itemsPerPage}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching users:", error);
  }
};

// ============= RIDER MANAGEMENT THUNKS =============

// 1. Get all riders with pending documents
export const getRidersPending = createAsyncThunk<
  GetRidersPendingResponse,
  void,
  { rejectValue: string }
>("riders/getRidersPending", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-pending`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRidersPendingResponse;
  } catch (error: any) {
    console.error("Error fetching pending riders:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch pending riders";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 2. Get rider statistics/overview
export const getRiderStats = createAsyncThunk<
  GetRiderStatsResponse,
  void,
  { rejectValue: string }
>("riders/getRiderStats", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-overview`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRiderStatsResponse;
  } catch (error: any) {
    console.error("Error fetching rider stats:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch rider statistics";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 3. Get rider by ID
export const getRiderById = createAsyncThunk<
  GetRiderByIdResponse,
  string,
  { rejectValue: string }
>("riders/getRiderById", async (riderId, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/get-rider/${riderId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRiderByIdResponse;
  } catch (error: any) {
    console.error(`Error fetching rider ${riderId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch rider details";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 4. Suspend rider
export const suspendRider = createAsyncThunk<
  { message: string },
  { riderId: string; reason: string },
  { rejectValue: string }
>("riders/suspendRider", async ({ riderId, reason }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_LOGISTIC_URL}/suspend-rider/${riderId}`,
      { reason } as SuspendRiderRequest,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    toast.success("Rider suspended successfully!");
    return response.data;
  } catch (error: any) {
    console.error(`Error suspending rider ${riderId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to suspend rider";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 5. Update rider
export const updateRider = createAsyncThunk<
  { message: string },
  { riderId: string; data: UpdateRiderRequest },
  { rejectValue: string }
>("riders/updateRider", async ({ riderId, data }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_LOGISTIC_URL}/update-rider/${riderId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    toast.success("Rider updated successfully!");
    return response.data;
  } catch (error: any) {
    console.error(`Error updating rider ${riderId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to update rider";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 6. Approve rider
export const approveRider = createAsyncThunk<
  ApproveRiderResponse,
  string,
  { rejectValue: string }
>("riders/approveRider", async (riderId, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_LOGISTIC_URL}/approve-rider/${riderId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    toast.success("Rider approved successfully!");
    return response.data as ApproveRiderResponse;
  } catch (error: any) {
    console.error(`Error approving rider ${riderId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to approve rider";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 7. Reject rider
export const rejectRider = createAsyncThunk<
  RejectRiderResponse,
  string,
  { rejectValue: string }
>("riders/rejectRider", async (riderId, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_LOGISTIC_URL}/reject-rider/${riderId}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    toast.success("Rider rejected successfully!");
    return response.data as RejectRiderResponse;
  } catch (error: any) {
    console.error(`Error rejecting rider ${riderId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to reject rider";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 8. Get riders overview
export const getRidersOverview = createAsyncThunk<
  GetRidersOverviewResponse,
  void,
  { rejectValue: string }
>("riders/getRidersOverview", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-overview`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRidersOverviewResponse;
  } catch (error: any) {
    console.error("Error fetching riders overview:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch riders overview";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 9. Get all riders stats with pagination
export const getAllRidersStats = createAsyncThunk<
  GetAllRidersStatsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>("riders/getAllRidersStats", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 5 } = params;
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-stat/?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetAllRidersStatsResponse;
  } catch (error: any) {
    console.error("Error fetching all riders stats:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch riders stats";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 10. Get recent activities
export const getRecentActivities = createAsyncThunk<
  GetRecentActivitiesResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>("logistics/getRecentActivities", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 10 } = params;
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/recent-activities?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRecentActivitiesResponse;
  } catch (error: any) {
    console.error("Error fetching recent activities:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch recent activities";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= REAL-TIME TRACKING THUNKS =============

// 1. Get all available riders
export const getAvailableRiders = createAsyncThunk<
  GetAvailableRidersResponse,
  void,
  { rejectValue: string }
>("realtime/getAvailableRiders", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/available-riders`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetAvailableRidersResponse;
  } catch (error: any) {
    console.error("Error fetching available riders:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch available riders";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 2. Get rider stats overview
export const getRiderStatsOverview = createAsyncThunk<
  GetRiderStatsOverviewResponse,
  void,
  { rejectValue: string }
>("realtime/getRiderStatsOverview", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/rider-stats-overview`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRiderStatsOverviewResponse;
  } catch (error: any) {
    console.error("Error fetching rider stats overview:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch rider stats overview";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 3. Get real-time stats with pagination
export const getRealTimeStats = createAsyncThunk<
  GetRealTimeStatsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>("realtime/getRealTimeStats", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 10 } = params;
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/real-time-stats?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetRealTimeStatsResponse;
  } catch (error: any) {
    console.error("Error fetching real-time stats:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch real-time stats";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= PAYOUT REQUEST THUNKS =============

// 1. Get pending withdrawals
export const getPendingWithdrawals = createAsyncThunk<
  GetPendingWithdrawalsResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>("payout/getPendingWithdrawals", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 20 } = params;
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/pending-withdrawals?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetPendingWithdrawalsResponse;
  } catch (error: any) {
    console.error("Error fetching pending withdrawals:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch pending withdrawals";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 2. Get specific withdrawal details
export const getWithdrawalDetails = createAsyncThunk<
  GetWithdrawalDetailsResponse,
  { riderId: string; withdrawalId: string },
  { rejectValue: string }
>(
  "payout/getWithdrawalDetails",
  async ({ riderId, withdrawalId }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_LOGISTIC_URL}/pending-withdrawal-details/${riderId}/${withdrawalId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data as GetWithdrawalDetailsResponse;
    } catch (error: any) {
      console.error(
        `Error fetching withdrawal details for ${riderId}/${withdrawalId}:`,
        error,
      );
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch withdrawal details";
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 3. Update payout status (approve/reject)
export const updatePayoutStatus = createAsyncThunk<
  UpdatePayoutStatusResponse,
  { riderId: string; withdrawalId: string; data: UpdatePayoutStatusRequest },
  { rejectValue: string }
>(
  "payout/updatePayoutStatus",
  async ({ riderId, withdrawalId, data }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_LOGISTIC_URL}/update-payout/${riderId}/${withdrawalId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      const action = data.action === "approve" ? "approved" : "rejected";
      toast.success(`Payout ${action} successfully!`);
      return response.data as UpdatePayoutStatusResponse;
    } catch (error: any) {
      console.error(
        `Error updating payout status for ${riderId}/${withdrawalId}:`,
        error,
      );
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update payout status";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 4. Get earning payout statistics
export const getEarningPayoutStats = createAsyncThunk<
  GetEarningPayoutStatsResponse,
  void,
  { rejectValue: string }
>("payout/getEarningPayoutStats", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/earning-payout-stat`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data as GetEarningPayoutStatsResponse;
  } catch (error: any) {
    console.error("Error fetching earning payout stats:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch earning payout statistics";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= ISSUES MANAGEMENT THUNKS =============

// 1. Get all issues reports with pagination
export const getIssuesReports = createAsyncThunk<
  IssuesReportsResponse,
  GetIssuesReportsParams,
  { rejectValue: string }
>("issues/getIssuesReports", async (params, thunkAPI) => {
  try {
    const { page = 1, limit = 10, status, search } = params;

    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (status && status !== "All Statuses") {
      queryParams.append("status", status);
    }

    if (search) {
      queryParams.append("search", search);
    }

    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/issues-reports?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as IssuesReportsResponse;
  } catch (error: any) {
    console.error("Error fetching issues reports:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch issues reports";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 2. Update issue status
export const updateIssueStatus = createAsyncThunk<
  UpdateIssueStatusResponse,
  { issueId: string; data: UpdateIssueStatusRequest },
  { rejectValue: string }
>("issues/updateIssueStatus", async ({ issueId, data }, thunkAPI) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_LOGISTIC_URL}/update-issue-status/${issueId}`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    toast.success("Issue status updated successfully!");
    return response.data as UpdateIssueStatusResponse;
  } catch (error: any) {
    console.error(`Error updating issue status ${issueId}:`, error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to update issue status";
    toast.error(`Error: ${errorMsg}`);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= LOGISTICS ORDER DISPATCH THUNKS =============

// Get logistics orders with pagination
export const getLogisticsOrders = createAsyncThunk<
  LogisticsOrdersResponse,
  { page?: number; limit?: number },
  { rejectValue: string }
>("logistics/getLogisticsOrders", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 10 } = params;

    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-orders?page=${page}&limit=${limit}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as LogisticsOrdersResponse;
  } catch (error: any) {
    console.error("Error fetching logistics orders:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch logistics orders";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= PERFORMANCE MONITORING THUNKS =============

// 1. Get riders performance data
export const getRidersPerformance = createAsyncThunk<
  GetRidersPerformanceResponse,
  { page?: number; limit?: number; search?: string; status?: string },
  { rejectValue: string }
>("performance/getRidersPerformance", async (params = {}, thunkAPI) => {
  try {
    const { page = 1, limit = 20, search, status } = params;

    // Build query parameters
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    if (search) {
      queryParams.append("search", search);
    }

    if (status && status !== "All Statuses") {
      queryParams.append("status", status);
    }

    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-performance?${queryParams.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as GetRidersPerformanceResponse;
  } catch (error: any) {
    console.error("Error fetching riders performance:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch riders performance data";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// 2. Get rider performance statistics and trends
export const getRidersStats = createAsyncThunk<
  GetRidersStatsResponse,
  void,
  { rejectValue: string }
>("performance/getRidersStats", async (_, thunkAPI) => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_LOGISTIC_URL}/riders-stats`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    return response.data as GetRidersStatsResponse;
  } catch (error: any) {
    console.error("Error fetching rider performance stats:", error);
    const errorMsg =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch rider performance statistics";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});


export const getInventoryOverview = createAsyncThunk(
  "inventory/getInventoryOverview",
  async (
    { page = 1, limit = 50 }: { page?: number; limit?: number } = {},
    thunkAPI,
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/inventory-overview?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      console.error("Error fetching inventory overview:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch inventory overview",
      );
    }
  },
);

export const getAllCustomerOrders = createAsyncThunk(
  "inventory/getAllCustomerOrders",
  async (filterType: string = "", thunkAPI) => {
    try {
      const url = filterType
        ? `${process.env.REACT_APP_API_URL}/all-customer-orders?filterType=${filterType}`
        : `${process.env.REACT_APP_API_URL}/all-customer-orders`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error fetching customer orders:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch customer orders",
      );
    }
  },
);

function normalizeAllNeededProductsResponse(
  raw: unknown,
  requestedPage: number,
  requestedLimit: number,
) {
  const r =
    raw && typeof raw === "object"
      ? (raw as Record<string, unknown>)
      : ({} as Record<string, unknown>);
  const data = Array.isArray(r.data) ? r.data : [];
  const num = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v) ? v : undefined;
  const page = num(r.page) ?? requestedPage;
  const limitRes = num(r.limit) ?? requestedLimit;
  const totalProducts =
    num(r.totalProducts) ??
    num(r.total_products) ??
    num(r.count) ??
    data.length;
  let totalPages = num(r.totalPages) ?? num(r.total_pages);
  if (totalPages == null || totalPages < 1) {
    totalPages = Math.max(
      1,
      Math.ceil(totalProducts / Math.max(1, limitRes)),
    );
  }
  const count = num(r.count) ?? totalProducts;
  return {
    success: Boolean(r.success),
    page,
    limit: limitRes,
    totalPages,
    totalProducts,
    count,
    data,
  };
}

// 3. Get All Needed Products
export const getAllNeededProducts = createAsyncThunk(
  "inventory/getAllNeededProducts",
  async (
    { page = 1, limit = 10 }: { page?: number; limit?: number },
    thunkAPI,
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/all-needed-products?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return normalizeAllNeededProductsResponse(response.data, page, limit);
    } catch (error: any) {
      console.error("Error fetching needed products:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch needed products",
      );
    }
  },
);

// 4. Get Updated Products
export const getUpdatedProducts = createAsyncThunk(
  "inventory/getUpdatedProducts",
  async (
    { filter }: { filter?: "today" | "weekly" | "monthly" } = {},
    thunkAPI,
  ) => {
    try {
      // Build URL with optional filter (no pagination - handled client-side)
      let url = `${process.env.REACT_APP_API_URL}/updated-products`;
      if (filter) {
        url += `?filter=${filter}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error("Error fetching updated products:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch updated products",
      );
    }
  },
);

// 5. Update Product Quantity
export const updateProductQuantity = createAsyncThunk(
  "inventory/updateProductQuantity",
  async (
    { productId, newQuantity }: { productId: string; newQuantity: number },
    thunkAPI,
  ) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/update-product-quantity/${productId}`,
        { newQuantity },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Product quantity updated successfully!");
      return response.data;
    } catch (error: any) {
      console.error("Error updating product quantity:", error);
      const errorMsg =
        error.response?.data?.message || "Failed to update product quantity";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 5b. Update Product Status (Availability)
export const updateProductStatus = createAsyncThunk(
  "inventory/updateProductStatus",
  async (
    { productId, isAvailable }: { productId: string; isAvailable: boolean },
    thunkAPI,
  ) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/updated-products-status/${productId}`,
        { isAvailable },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Product availability status updated successfully!");
      return response.data;
    } catch (error: any) {
      console.error("Error updating product status:", error);
      const errorMsg =
        error.response?.data?.message || "Failed to update product status";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 5c. Update All Products Threshold
export const updateAllProductsThreshold = createAsyncThunk(
  "inventory/updateAllProductsThreshold",
  async ({ threshold }: { threshold: number }, thunkAPI) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/products/threshold/set`,
        { threshold },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        response.data?.message || `All product thresholds updated to ${threshold}`,
      );
      return { ...response.data, threshold };
    } catch (error: any) {
      console.error("Error updating all product thresholds:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to update product thresholds";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 5d. Update Single Product Threshold
export const updateProductThreshold = createAsyncThunk(
  "inventory/updateProductThreshold",
  async (
    { productId, threshold }: { productId: string; threshold: number },
    thunkAPI,
  ) => {
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/products/${productId}/threshold`,
        { threshold },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(
        response.data?.message || "Product threshold updated successfully",
      );
      return { ...response.data, productId, threshold };
    } catch (error: any) {
      console.error("Error updating product threshold:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to update product threshold";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 6. Get All Emails
export const getEmails = createAsyncThunk(
  "inventory/getEmails",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/get-all-emails`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      console.error("Error fetching emails:", error);
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch emails",
      );
    }
  },
);

// 7. Add Email
export const addEmail = createAsyncThunk(
  "inventory/addEmail",
  async (email: string, thunkAPI) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/add-email`,
        { email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Email added successfully!");
      return response.data;
    } catch (error: any) {
      console.error("Error adding email:", error);
      const errorMsg = error.response?.data?.message || "Failed to add email";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// 8. Delete Email
export const deleteEmail = createAsyncThunk(
  "inventory/deleteEmail",
  async (emailId: string, thunkAPI) => {
    try {
      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/delete-email/${emailId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success("Email deleted successfully!");
      return response.data;
    } catch (error: any) {
      console.error("Error deleting email:", error);
      const errorMsg =
        error.response?.data?.message || "Failed to delete email";
      toast.error(`Error: ${errorMsg}`);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= REWARDS AND SPIN AND WIN THUNKS =============

export const getRewardsDashboardStats = createAsyncThunk(
  "rewards/getDashboardStats",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/spin-and-win/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch dashboard stats",
      );
    }
  },
);

export const getActiveRewards = createAsyncThunk(
  "rewards/getActiveRewards",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/active-rewards`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch active rewards",
      );
    }
  },
);

export const getRewardRecentActivities = createAsyncThunk(
  "rewards/getRecentActivities",
  async (
    params: { page?: number; limit?: number } = { page: 1, limit: 10 },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const { page = 1, limit = 10 } = params || {};
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/recent-activities?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch recent activities",
      );
    }
  },
);

export const getWeeklyDiscountTrend = createAsyncThunk(
  "rewards/getWeeklyDiscountTrend",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/analytics/weekly-discount-trend`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch weekly discount trend",
      );
    }
  },
);

export const getWeeklyEngagementTrend = createAsyncThunk(
  "rewards/getWeeklyEngagementTrend",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/analytics/weekly-engagement-trend`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch weekly engagement trend",
      );
    }
  },
);

export const createPromoCode = createAsyncThunk(
  "rewards/createPromoCode",
  async (payload: CreatePromoCodePayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/campaigns/create-campaign`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Promo code created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to create promo code";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllPromoCodes = createAsyncThunk(
  "rewards/getAllPromoCodes",
  async (
    { page = 1, limit = 10 }: { page?: number; limit?: number },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/promo-codes?page=${page}&limit=${limit}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch promo codes",
      );
    }
  },
);

export const getAllDiscounts = createAsyncThunk(
  "rewards/getAllDiscounts",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discounts/all`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch discounts",
      );
    }
  },
);

// ============= CAMPAIGN THUNKS =============

export const createCampaign = createAsyncThunk(
  "rewards/createCampaign",
  async (payload: CreateCampaignPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/campaigns/create-campaign`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Campaign created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to create campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllCampaigns = createAsyncThunk(
  "rewards/getAllCampaigns",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaigns`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch campaigns",
      );
    }
  },
);

export const getActiveCampaigns = createAsyncThunk(
  "rewards/getActiveCampaigns",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaigns/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch active campaigns",
      );
    }
  },
);

export const getInactiveCampaigns = createAsyncThunk(
  "rewards/getInactiveCampaigns",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaigns/inactive`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch inactive campaigns",
      );
    }
  },
);

export const getExpiredCampaigns = createAsyncThunk(
  "rewards/getExpiredCampaigns",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaigns/expired`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch expired campaigns",
      );
    }
  },
);

export const getSingleCampaign = createAsyncThunk(
  "rewards/getSingleCampaign",
  async (campaignId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch campaign details",
      );
    }
  },
);

export const updateCampaign = createAsyncThunk(
  "rewards/updateCampaign",
  async (
    { campaignId, payload }: { campaignId: string; payload: any },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Campaign updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const toggleCampaignStatus = createAsyncThunk(
  "rewards/toggleCampaignStatus",
  async (
    { campaignId, isActive }: { campaignId: string; isActive: boolean },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`,
        { isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Campaign ${isActive ? "enabled" : "disabled"} successfully`,
      );
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update campaign status";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteCampaign = createAsyncThunk(
  "rewards/deleteCampaign",
  async (campaignId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/campaigns/${campaignId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Campaign deleted successfully");
      return campaignId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= DISCOUNT THUNKS =============

export const createDiscount = createAsyncThunk(
  "rewards/createDiscount",
  async (payload: CreateDiscountPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/discounts/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to create discount";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getSingleDiscount = createAsyncThunk(
  "rewards/getSingleDiscount",
  async (discountId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discounts/${discountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch discount details",
      );
    }
  },
);

export const updateDiscount = createAsyncThunk(
  "rewards/updateDiscount",
  async (
    { discountId, payload }: { discountId: string; payload: any },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/discounts/${discountId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update discount";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const toggleDiscountStatus = createAsyncThunk(
  "rewards/toggleDiscountStatus",
  async (
    { discountId, isActive }: { discountId: string; isActive: boolean },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/discounts/${discountId}`,
        { isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(
        `Discount ${isActive ? "enabled" : "disabled"} successfully`,
      );
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update discount status";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteDiscount = createAsyncThunk(
  "rewards/deleteDiscount",
  async (discountId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/discounts/${discountId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount deleted successfully");
      return discountId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete discount";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const toggleDiscountFunctionScope = createAsyncThunk<
  any,
  { discountId: string; scopeId: string; isAdminActive: boolean },
  { rejectValue: string }
>(
  "rewards/toggleDiscountFunctionScope",
  async ({ discountId, scopeId, isAdminActive }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/discounts/${discountId}/function/${scopeId}/admin-active`,
        { isAdminActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Function scope updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update function scope";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= FREE DELIVERY THUNKS =============

export const createFreeDelivery = createAsyncThunk(
  "rewards/createFreeDelivery",
  async (payload: CreateFreeDeliveryPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/free-delivery/create-free-delivery`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery campaign created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to create free delivery campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllFreeDeliveries = createAsyncThunk(
  "rewards/getAllFreeDeliveries",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/free-delivery`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch free deliveries",
      );
    }
  },
);

export const getSingleFreeDelivery = createAsyncThunk(
  "rewards/getSingleFreeDelivery",
  async (deliveryId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/free-delivery/${deliveryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch free delivery details",
      );
    }
  },
);

export const updateFreeDelivery = createAsyncThunk(
  "rewards/updateFreeDelivery",
  async (
    { deliveryId, payload }: { deliveryId: string; payload: any },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/free-delivery/${deliveryId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery campaign updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to update free delivery campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const toggleFreeDeliveryStatus = createAsyncThunk(
  "rewards/toggleFreeDeliveryStatus",
  async (
    { freeDeliveryId, isActive }: { freeDeliveryId: string; isActive: boolean },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/free-delivery/${freeDeliveryId}`,
        { isActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery status updated");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to toggle status";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteFreeDelivery = createAsyncThunk(
  "rewards/deleteFreeDelivery",
  async (freeDeliveryId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/free-delivery/${freeDeliveryId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery campaign deleted successfully");
      return freeDeliveryId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete campaign";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= PROMO CODE FUNCTION THUNKS =============

export const createPromoCodeFunction = createAsyncThunk(
  "rewards/createPromoCodeFunction",
  async (payload: CreatePromoCodeFunctionPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/promo-code-functions/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Promo code function created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to create promo code function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllPromoCodeFunctions = createAsyncThunk(
  "rewards/getAllPromoCodeFunctions",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/promo-code-functions`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch promo code functions",
      );
    }
  },
);

export const getPromoCodeFunctionById = createAsyncThunk(
  "rewards/getPromoCodeFunctionById",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/promo-code-functions/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch promo code function details",
      );
    }
  },
);

export const updatePromoCodeFunction = createAsyncThunk(
  "rewards/updatePromoCodeFunction",
  async (
    {
      functionId,
      payload,
    }: { functionId: string; payload: UpdatePromoCodeFunctionPayload },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/promo-code-functions/${functionId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Promo code function updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update promo code function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deletePromoCodeFunction = createAsyncThunk(
  "rewards/deletePromoCodeFunction",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/promo-code-functions/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Promo code function deleted successfully");
      return functionId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete promo code function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= DISCOUNT FUNCTION THUNKS =============

export const createDiscountFunction = createAsyncThunk(
  "rewards/createDiscountFunction",
  async (payload: CreateDiscountFunctionPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/discount-function/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount function created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to create discount function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllDiscountFunctions = createAsyncThunk(
  "rewards/getAllDiscountFunctions",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discount-function`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to fetch discount functions",
      );
    }
  },
);

export const getDiscountFunctionById = createAsyncThunk(
  "rewards/getDiscountFunctionById",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/discount-function/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch discount function details",
      );
    }
  },
);

export const updateDiscountFunction = createAsyncThunk(
  "rewards/updateDiscountFunction",
  async (
    {
      functionId,
      payload,
    }: { functionId: string; payload: UpdateDiscountFunctionPayload },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/discount-function/${functionId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount function updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update discount function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteDiscountFunction = createAsyncThunk(
  "rewards/deleteDiscountFunction",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/discount-function/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Discount function deleted successfully");
      return functionId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to delete discount function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= FREE DELIVERY FUNCTION THUNKS =============

export const createFreeDeliveryFunction = createAsyncThunk(
  "rewards/createFreeDeliveryFunction",
  async (payload: CreateFreeDeliveryFunctionPayload, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/free-delivery-function/create`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery function created successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to create free delivery function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const getAllFreeDeliveryFunctions = createAsyncThunk(
  "rewards/getAllFreeDeliveryFunctions",
  async (_, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/free-delivery-function`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch free delivery functions",
      );
    }
  },
);

export const getFreeDeliveryFunctionById = createAsyncThunk(
  "rewards/getFreeDeliveryFunctionById",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/free-delivery-function/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message ||
        "Failed to fetch free delivery function details",
      );
    }
  },
);

export const updateFreeDeliveryFunction = createAsyncThunk(
  "rewards/updateFreeDeliveryFunction",
  async (
    {
      functionId,
      payload,
    }: { functionId: string; payload: UpdateFreeDeliveryFunctionPayload },
    thunkAPI,
  ) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/free-delivery-function/${functionId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery function updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to update free delivery function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteFreeDeliveryFunction = createAsyncThunk(
  "rewards/deleteFreeDeliveryFunction",
  async (functionId: string, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/free-delivery-function/${functionId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Free delivery function deleted successfully");
      return functionId;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message ||
        "Failed to delete free delivery function";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

// ============= GAMIFICATION THUNKS =============

export const getGamificationConfig = createAsyncThunk<
  GamificationConfigResponse,
  void,
  { rejectValue: string }
>("rewards/getGamificationConfig", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/gamification/config`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data as GamificationConfigResponse;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message || "Failed to fetch gamification config";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const updateGamificationConfig = createAsyncThunk<
  GamificationConfigResponse,
  UpdateGamificationConfigPayload,
  { rejectValue: string }
>("rewards/updateGamificationConfig", async (payload, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_API_URL}/gamification/config`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    toast.success("Gamification config updated successfully");
    return response.data as GamificationConfigResponse;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message || "Failed to update gamification config";
    toast.error(errorMsg);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

// ============= CHECKOUT CATEGORY THUNKS =============

export const getAllCheckoutCategories = createAsyncThunk<
  CheckoutCategoriesResponse,
  void,
  { rejectValue: string }
>("rewards/getAllCheckoutCategories", async (_, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/admin/checkout-category`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data as CheckoutCategoriesResponse;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message || "Failed to fetch checkout categories";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const createCheckoutCategory = createAsyncThunk<
  SingleCheckoutCategoryResponse,
  CreateCheckoutCategoryPayload,
  { rejectValue: string }
>("rewards/createCheckoutCategory", async (payload, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/admin/checkout-category`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    toast.success("Checkout category created successfully");
    return response.data as SingleCheckoutCategoryResponse;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message || "Failed to create checkout category";
    toast.error(errorMsg);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const updateCheckoutCategory = createAsyncThunk<
  SingleCheckoutCategoryResponse,
  { categoryId: string; payload: any },
  { rejectValue: string }
>(
  "rewards/updateCheckoutCategory",
  async ({ categoryId, payload }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/checkout-category/${categoryId}`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // toast.success("Checkout category updated successfully");
      return response.data as SingleCheckoutCategoryResponse;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update checkout category";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

export const deleteCheckoutCategory = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("rewards/deleteCheckoutCategory", async (categoryId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}/admin/checkout-category/${categoryId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    toast.success("Checkout category deleted successfully");
    return categoryId;
  } catch (error: any) {
    const errorMsg =
      error.response?.data?.message || "Failed to delete checkout category";
    toast.error(errorMsg);
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const toggleCheckoutCategoryItem = createAsyncThunk<
  any,
  { categoryId: string; itemId: string; isAdminActive: boolean },
  { rejectValue: string }
>(
  "rewards/toggleCheckoutCategoryItem",
  async ({ categoryId, itemId, isAdminActive }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/admin/checkout-category/${categoryId}/item/${itemId}/admin-active`,
        { isAdminActive },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Item admin status updated successfully");
      return response.data;
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Failed to update item admin status";
      toast.error(errorMsg);
      return thunkAPI.rejectWithValue(errorMsg);
    }
  },
);

const getRestaurantUploadFileUrl = (): string => {
  const explicit = process.env.REACT_APP_RESTAURANT_UPLOAD_URL;
  if (explicit) return explicit;

  const restaurantBase = process.env.REACT_APP_RESTAURANT_URL;
  if (restaurantBase) {
    return `${restaurantBase
      .replace(/\/v1\/api\/?$/, "")
      .replace(/\/+$/, "")}/upload/file`;
  }

  const apiUrl = process.env.REACT_APP_API_URL || "";
  const isProdApi =
    apiUrl.includes("api.foodbank4u.com") &&
    !apiUrl.includes("api.dev.foodbank4u.com");
  if (isProdApi) {
    return "https://api.dev.foodbank4u.com/restaurant/upload/file";
  }
  return "https://api.dev.foodbank4u.com/restaurant/upload/file";
};

export const uploadFile = createAsyncThunk<
  { url: string; message: string },
  { file: File; type: "image" | "video" | "pdf" },
  { rejectValue: string }
>("assets/uploadFile", async ({ file, type }, thunkAPI) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("type", type);

  const uploadUrl = getRestaurantUploadFileUrl();

  try {
    const response = await axios.post(uploadUrl, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    const errorMsg = error.response?.data?.message || "Failed to upload file";
    return thunkAPI.rejectWithValue(errorMsg);
  }
});

export const getAllContactMessages = createAsyncThunk<
  GetAllContactMessagesResponse,
  { page: number },
  { rejectValue: string }
>("contactMessages/getAllContactMessages", async ({ page }, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/all-contact-messages?page=${page}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error: any) {
    const errMsg = error.response?.data?.message || "Failed to fetch messages";
    return thunkAPI.rejectWithValue(errMsg);
  }
});

export const getSingleContactMessage = createAsyncThunk<
  GetSingleContactMessageResponse,
  string,
  { rejectValue: string }
>("contactMessages/getSingleContactMessage", async (contactId, thunkAPI) => {
  const auth: any = sessionStorage.getItem("authState");
  const jsonData = JSON.parse(auth || "{}");
  const token = jsonData?.token;
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/contacts/${contactId}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    return response.data;
  } catch (error: any) {
    const errMsg = error.response?.data?.message || "Failed to fetch message";
    return thunkAPI.rejectWithValue(errMsg);
  }
});

export const replyToContactMessage = createAsyncThunk<
  ReplyContactMessageResponse,
  { contactId: string; message: string },
  { rejectValue: string }
>(
  "contactMessages/replyToContactMessage",
  async ({ contactId, message }, thunkAPI) => {
    const auth: any = sessionStorage.getItem("authState");
    const jsonData = JSON.parse(auth || "{}");
    const token = jsonData?.token;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/contacts/${contactId}/reply`,
        { message },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("Reply sent successfully");
      return response.data;
    } catch (error: any) {
      const errMsg = error.response?.data?.message || "Failed to send reply";
      toast.error(errMsg);
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);



export const getOrderProcessingList = createAsyncThunk(
  "orderProcessing/getList",
  async (
    {
      page = 1,
      limit = 10,
      orderType,
      adminStatus,
    }: {
      page?: number;
      limit?: number;
      orderType?: string;
      adminStatus?: string;
    } = {},
    thunkAPI,
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (orderType) params.set("orderType", orderType);
      if (adminStatus) params.set("adminStatus", adminStatus);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/order/processing/admin-status?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message || "Failed to fetch orders";
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);

export const searchOrderProcessing = createAsyncThunk(
  "orderProcessing/search",
  async (
    { status, email }: { status: string; email: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/order/processing/admin-status/${status}/search?email=${encodeURIComponent(email)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message || "Failed to search orders";
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);

export const getOrderProcessingStats = createAsyncThunk(
  "orderProcessing/getStats",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/order/processing/admin-status/statistics/overview`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message || "Failed to fetch order statistics";
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);

export const getOrderProcessingSingle = createAsyncThunk(
  "orderProcessing/getSingle",
  async ({ orderId }: { orderId: string }, thunkAPI) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/order/processing/single/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      return response.data;
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message || "Failed to fetch order details";
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);

export const updateOrderProcessingStatus = createAsyncThunk(
  "orderProcessing/updateStatus",
  async (
    { orderId, adminStatus }: { orderId: string; adminStatus: string },
    thunkAPI,
  ) => {
    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/order/processing/admin-status/update/${orderId}`,
        { adminStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      toast.success(response.data?.message || "Order status updated");
      return response.data;
    } catch (error: any) {
      const errMsg =
        error.response?.data?.message || "Failed to update order status";
      toast.error(errMsg);
      return thunkAPI.rejectWithValue(errMsg);
    }
  },
);

